import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Cpu, MemoryStick, HardDrive, Database } from "lucide-react";

const kpiData = {
  cpu: [
    { name: "Peak CPU Utilization", target: "<85% peak", actual: "81%" },
    { name: "CPU Wait Time", target: "<5%", actual: "3.2%" },
    { name: "Parallelism Efficiency", target: ">80%", actual: "85%" },
    { name: "Context Switching", target: "<10K/sec/core", actual: "8.5K" }
  ],
  memory: [
    { name: "Buffer Cache Hit Ratio", target: ">95%", actual: "96.1%" },
    { name: "Page Life Expectancy", target: ">600s", actual: "645s" },
    { name: "Memory Grant Waits", target: "<5%", actual: "2.1%" },
    { name: "Memory Pressure Events", target: "0", actual: "0" }
  ],
  io: [
    { name: "Disk Read Latency", target: "<50ms max", actual: "42ms" },
    { name: "Disk Write Latency", target: "<30ms max", actual: "28ms" },
    { name: "I/O Throughput", target: ">1000 IOPS", actual: "1,180 IOPS" },
    { name: "I/O Wait Time", target: "<20%", actual: "15%" }
  ],
  tempdb: [
    { name: "TempDB Space Usage", target: "<80%", actual: "72%" },
    { name: "Allocation Contention", target: "<5%", actual: "2.8%" },
    { name: "Auto-Growth Events", target: "0", actual: "0" },
    { name: "Version Store Usage", target: "<20%", actual: "12%" }
  ]
};

const icons = {
  cpu: <Cpu className="w-5 h-5 text-orange-600" />,
  memory: <MemoryStick className="w-5 h-5 text-green-600" />,
  io: <HardDrive className="w-5 h-5 text-shiftleft-600" />,
  tempdb: <Database className="w-5 h-5 text-purple-600" />
};

export const StressTabKPIs = ({ type }: { type: keyof typeof kpiData }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    {kpiData[type].map((kpi, i) => (
      <Card key={i} className="shadow border border-gray-200 dark:border-gray-800">
        <CardHeader className="flex flex-row items-center gap-3 pb-2">
          <div>{icons[type]}</div>
          <CardTitle className="text-base font-semibold text-gray-900 dark:text-white">
            {kpi.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Target</span>
            <span>Actual</span>
          </div>
          <div className="flex items-center justify-between text-lg font-mono">
            <span className="text-gray-700 dark:text-gray-200">{kpi.target}</span>
            <span className="text-shiftleft-700 dark:text-shiftleft-300 font-bold">{kpi.actual}</span>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const StressOverviewKPIs = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
    {Object.entries(kpiData).map(([type, kpis]) => (
      <Card key={type} className="shadow border border-gray-200 dark:border-gray-800">
        <CardHeader className="flex flex-row items-center gap-3 pb-2">
          <div>{icons[type as keyof typeof icons]}</div>
          <CardTitle className="text-base font-semibold text-gray-900 dark:text-white capitalize">
            {type} KPIs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3">
          <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
            {(kpis as any[]).map((k, i) => (
              <li key={i}>
                <span className="font-medium text-gray-800 dark:text-gray-100">{k.name}:</span> Target <span className="text-gray-700">{k.target}</span>, Actual <span className="text-shiftleft-700 dark:text-shiftleft-300 font-bold">{k.actual}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    ))}
  </div>
);
